/**
 * Native OAuth flow. On web we simply navigate the page to the provider's
 * start URL (the server bounces back with a #auth=<jwt> fragment). On native
 * (Capacitor) we cannot navigate the app's own WebView away — it would load
 * the web SPA and never return. Instead we:
 *
 *   1. Open the provider start URL (with state=native) in the system browser.
 *   2. The server callback redirects to `games.dontmiss.app://auth#auth=<jwt>`.
 *   3. iOS/Android re-open the app via the custom URL scheme; the `appUrlOpen`
 *      listener below extracts the token and resolves.
 */
import { Capacitor } from '@capacitor/core';
import { authApi } from './authApi';

export type OAuthResult =
  | { ok: true; token: string; uid: string }
  | { ok: false; error: string };

function isNative(): boolean {
  const p = Capacitor.getPlatform();
  return p === 'ios' || p === 'android';
}

function parseAuthHash(rawUrl: string): OAuthResult | null {
  const hashIdx = rawUrl.indexOf('#');
  if (hashIdx < 0) return null;
  const params = new URLSearchParams(rawUrl.slice(hashIdx + 1));
  const err = params.get('auth_error');
  if (err) return { ok: false, error: err };
  const token = params.get('auth');
  const uid = params.get('uid');
  if (token && uid) return { ok: true, token, uid };
  return null;
}

/**
 * Runs the OAuth dance. On web this never resolves (the page navigates away and
 * the boot-time hash handler takes over). On native it resolves with the token.
 */
export async function startNativeOAuth(
  provider: 'google',
): Promise<OAuthResult> {
  const base = authApi.startOAuthUrl(provider);

  if (!isNative()) {
    // Web: classic full-page redirect; useAuthBootstrap consumes the #auth hash.
    window.location.href = base;
    return new Promise<OAuthResult>(() => {}); // never resolves; page unloads
  }

  const { Browser } = await import('@capacitor/browser');
  const { App } = await import('@capacitor/app');

  const startUrl = `${base}?state=native`;

  return new Promise<OAuthResult>((resolve) => {
    let settled = false;
    const finish = (r: OAuthResult) => {
      if (settled) return;
      settled = true;
      void listener.then((l) => l.remove());
      void closeListener.then((l) => l.remove());
      void Browser.close().catch(() => { /* already closed */ });
      resolve(r);
    };

    const listener = App.addListener('appUrlOpen', (event) => {
      const result = parseAuthHash(event.url);
      if (result) finish(result);
    });

    // If the user dismisses the system browser without completing sign-in, the
    // deep-link callback never fires — so resolve as cancelled, otherwise the
    // caller awaits forever and the login button spins indefinitely. A short
    // grace period lets a genuine success (appUrlOpen) win the race when the
    // browser auto-closes on the redirect.
    const closeListener = Browser.addListener('browserFinished', () => {
      window.setTimeout(() => finish({ ok: false, error: 'cancelled' }), 600);
    });

    void Browser.open({ url: startUrl, presentationStyle: 'popover' });
  });
}
